// Knowledge Base Articles Management Page
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Menu,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  MoreVert,
  Article as ArticleIcon,
  ThumbUp,
  RemoveRedEye,
} from '@mui/icons-material';
import { useOrganization } from '@/contexts/OrganizationContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/contexts/ToastContext';
import { articlesService } from '@/services/collections/articles';
import { formatRelativeTime } from '@/services/firebase/firestore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { Article, ArticleStatus } from '@/types';

const STATUS_COLORS: Record<ArticleStatus, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

export const ArticlesPage = () => {
  const navigate = useNavigate();
  const { organization, project } = useOrganization();
  const { canCreateArticles, canEditArticles, canDeleteArticles, canPublishArticles, hasKnowledgeBase } =
    usePermissions();
  const { showSuccess, showError } = useToast();

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; article: Article } | null>(null);

  // Fetch articles
  const fetchArticles = async () => {
    if (!organization || !project) return;

    setLoading(true);
    try {
      let data: Article[] = [];

      switch (tab) {
        case 0: // All
          data = await articlesService.getByProject(organization.id, project.id, []);
          break;
        case 1: // Published
          data = await articlesService.getByStatus(organization.id, project.id, 'published');
          break;
        case 2: // Drafts
          data = await articlesService.getByStatus(organization.id, project.id, 'draft');
          break;
        case 3: // Archived
          data = await articlesService.getByStatus(organization.id, project.id, 'archived');
          break;
      }

      setArticles(data);
    } catch (error) {
      console.error('Error fetching articles:', error);
      showError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [organization, project, tab]);

  // Filter articles
  const filteredArticles = articles.filter((article) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      article.title.toLowerCase().includes(query) ||
      article.content.toLowerCase().includes(query) ||
      article.category?.toLowerCase().includes(query)
    );
  });

  // Publish article
  const handlePublish = async (article: Article) => {
    try {
      await articlesService.publish(article.id);
      showSuccess('Article published');
      fetchArticles();
    } catch (error) {
      showError('Failed to publish article');
    }
    setMenuAnchor(null);
  };

  // Unpublish article
  const handleUnpublish = async (article: Article) => {
    try {
      await articlesService.unpublish(article.id);
      showSuccess('Article unpublished');
      fetchArticles();
    } catch (error) {
      showError('Failed to unpublish article');
    }
    setMenuAnchor(null);
  };

  // Archive article
  const handleArchive = async (article: Article) => {
    try {
      await articlesService.archive(article.id);
      showSuccess('Article archived');
      fetchArticles();
    } catch (error) {
      showError('Failed to archive article');
    }
    setMenuAnchor(null);
  };

  // Delete article
  const handleDelete = async (article: Article) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await articlesService.delete(article.id);
      showSuccess('Article deleted');
      fetchArticles();
    } catch (error) {
      showError('Failed to delete article');
    }
    setMenuAnchor(null);
  };

  // Check plan access
  if (!hasKnowledgeBase) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Knowledge Base
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Upgrade to Pro or higher to access the Knowledge Base feature.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/app/settings/billing')}>
          Upgrade Plan
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Knowledge Base
        </Typography>
        {canCreateArticles && (
          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/app/articles/new')}>
            New Article
          </Button>
        )}
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All" />
        <Tab label="Published" />
        <Tab label="Drafts" />
        <Tab label="Archived" />
      </Tabs>

      {/* Search */}
      <TextField
        placeholder="Search articles..."
        size="small"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, width: 300 }}
      />

      {/* Articles Grid */}
      {loading ? (
        <LoadingSpinner message="Loading articles..." />
      ) : filteredArticles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No articles found
          </Typography>
          {canCreateArticles && (
            <Button startIcon={<Add />} onClick={() => navigate('/app/articles/new')} sx={{ mt: 2 }}>
              Create your first article
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredArticles.map((article) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={article.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                }}
                onClick={() => navigate(`/app/articles/${article.id}`)}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Chip
                      label={article.status}
                      size="small"
                      color={STATUS_COLORS[article.status]}
                    />
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuAnchor({ el: e.currentTarget, article });
                      }}
                    >
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </Box>

                  <Typography variant="h6" gutterBottom noWrap>
                    {article.title}
                  </Typography>

                  {article.category && (
                    <Chip label={article.category} size="small" variant="outlined" sx={{ mb: 1 }} />
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {article.excerpt || article.content.slice(0, 150)}
                  </Typography>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <RemoveRedEye sx={{ fontSize: 16 }} />
                      <Typography variant="caption">{article.viewCount || 0}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ThumbUp sx={{ fontSize: 16 }} />
                      <Typography variant="caption">{article.helpfulCount || 0}</Typography>
                    </Box>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {formatRelativeTime(article.updatedAt)}
                  </Typography>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Article Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        {menuAnchor?.article.status === 'draft' && canPublishArticles && (
          <MenuItem onClick={() => handlePublish(menuAnchor.article)}>
            <Visibility sx={{ mr: 1 }} fontSize="small" /> Publish
          </MenuItem>
        )}
        {menuAnchor?.article.status === 'published' && canPublishArticles && (
          <MenuItem onClick={() => handleUnpublish(menuAnchor.article)}>
            <VisibilityOff sx={{ mr: 1 }} fontSize="small" /> Unpublish
          </MenuItem>
        )}
        {canEditArticles && (
          <MenuItem
            onClick={() => {
              navigate(`/app/articles/${menuAnchor?.article.id}/edit`);
              setMenuAnchor(null);
            }}
          >
            <Edit sx={{ mr: 1 }} fontSize="small" /> Edit
          </MenuItem>
        )}
        {menuAnchor?.article.status !== 'archived' && canEditArticles && (
          <MenuItem onClick={() => handleArchive(menuAnchor!.article)}>
            Archive
          </MenuItem>
        )}
        {canDeleteArticles && (
          <MenuItem onClick={() => handleDelete(menuAnchor!.article)} sx={{ color: 'error.main' }}>
            <Delete sx={{ mr: 1 }} fontSize="small" /> Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
